import { computed, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useInternalChatAudio } from './useInternalChatAudio';
import {
  legacyMentionAttributes,
  reconciledMentionAttributes,
} from './internalChatMentions';

export const useInternalChatComposer = (props, emit, draft) => {
  const { t } = useI18n();
  const content = ref('');
  const contentFormat = ref('markdown');
  const isContentEmpty = ref(true);
  let hasLocalEdits = false;
  const attachments = ref([]);
  const fileInput = ref(null);
  const textInput = ref(null);
  const {
    audioVisible,
    audioBusy,
    audioError,
    startAudio,
    discardAudio,
    finishAudio,
    failAudio,
    removeAudio,
    resetAudio,
  } = useInternalChatAudio(attachments, textInput, emit);
  const resizableEditor = ref(null);
  const editorExpanded = computed(() =>
    Boolean(resizableEditor.value?.isExpanded)
  );
  const editorSizeLabel = computed(() =>
    editorExpanded.value
      ? t('INTERNAL_CHAT.COLLAPSE_EDITOR')
      : t('INTERNAL_CHAT.EXPAND_EDITOR')
  );
  const toggleEditorSize = () => {
    resizableEditor.value?.toggleEditorExpand();
    nextTick(() => textInput.value?.focus());
  };
  const syncingDraft = ref(false);
  const mentionMenuOpen = computed(() =>
    Boolean(textInput.value?.mentionMenuOpen)
  );
  const toggleMentionMenu = () => textInput.value?.toggleMentionMenu();
  const mentionedUserIds = ref([]);
  const mentionedTeamIds = ref([]);
  const mentionAll = ref(false);
  const alsoSendInChannel = ref(false);
  const reconciledMentions = computed(() =>
    reconciledMentionAttributes({
      content: content.value,
      contentFormat: contentFormat.value,
      members: props.members,
      previous: {
        mentioned_user_ids: mentionedUserIds.value,
        mentioned_team_ids: mentionedTeamIds.value,
        mention_all: mentionAll.value,
      },
    })
  );

  watch(
    draft,
    value => {
      if (hasLocalEdits) return;
      syncingDraft.value = true;
      content.value = value?.content || '';
      contentFormat.value =
        value?.content_attributes?.content_format ||
        (value ? 'plain' : 'markdown');
      isContentEmpty.value = !content.value.trim();
      const attributes = legacyMentionAttributes(value, {
        expandedRecipients: false,
      });
      mentionedUserIds.value = Array.from(
        attributes.mentioned_user_ids || []
      ).map(Number);
      mentionedTeamIds.value = Array.from(
        attributes.mentioned_team_ids || []
      ).map(Number);
      mentionAll.value = Boolean(attributes.mention_all);
      alsoSendInChannel.value = Boolean(
        value?.content_attributes?.also_send_in_channel
      );
      nextTick(() => {
        syncingDraft.value = false;
      });
    },
    { immediate: true }
  );

  const contentAttributes = computed(() => ({
    content_format: contentFormat.value,
    mentioned_user_ids: reconciledMentions.value.mentionedUserIds,
    mentioned_team_ids: reconciledMentions.value.mentionedTeamIds,
    ...(reconciledMentions.value.mentionAll ? { mention_all: true } : {}),
    ...(props.isThread && alsoSendInChannel.value
      ? { also_send_in_channel: true }
      : {}),
  }));

  const emitDraft = () => {
    if (!syncingDraft.value) {
      emit('draftChange', {
        content: content.value,
        contentAttributes: contentAttributes.value,
      });
    }
  };

  watch(
    [
      content,
      contentFormat,
      mentionedUserIds,
      mentionedTeamIds,
      mentionAll,
      alsoSendInChannel,
    ],
    emitDraft
  );

  const contentLength = computed(() => Array.from(content.value).length);
  const validationError = computed(() =>
    contentLength.value > 10_000
      ? t('INTERNAL_CHAT.CONTENT_TOO_LONG', {
          count: contentLength.value,
          limit: 10_000,
        })
      : ''
  );
  const canSend = computed(
    () =>
      !validationError.value &&
      (!isContentEmpty.value || attachments.value.length > 0)
  );

  const openFilePicker = () => fileInput.value?.click();

  const onFilesSelected = event => {
    hasLocalEdits = true;
    attachments.value = [
      ...attachments.value,
      ...Array.from(event.target.files || []),
    ];
    event.target.value = '';
  };

  const removeAttachment = file => {
    hasLocalEdits = true;
    removeAudio(file);
    attachments.value = attachments.value.filter(item => item !== file);
  };

  const submit = () => {
    if (props.disabled || audioBusy.value || !canSend.value) return;
    emit('typing', false);
    emit('send', {
      content: content.value,
      contentFormat: contentFormat.value,
      attachments: attachments.value,
      ...reconciledMentions.value,
      alsoSendInChannel: props.isThread && alsoSendInChannel.value,
    });
  };

  const reset = () => {
    resetAudio();
    resizableEditor.value?.resetEditorHeight();
    syncingDraft.value = true;
    hasLocalEdits = false;
    content.value = '';
    contentFormat.value = 'markdown';
    isContentEmpty.value = true;
    attachments.value = [];
    mentionedUserIds.value = [];
    mentionedTeamIds.value = [];
    mentionAll.value = false;
    alsoSendInChannel.value = false;
    emit('typing', false);
    nextTick(() => {
      syncingDraft.value = false;
    });
  };

  const onInput = value => {
    hasLocalEdits = true;
    content.value = value;
  };

  const payloadMatches = payload =>
    content.value === payload.content &&
    contentFormat.value === payload.contentFormat &&
    reconciledMentions.value.mentionedUserIds.join(',') ===
      (payload.mentionedUserIds || []).join(',') &&
    reconciledMentions.value.mentionedTeamIds.join(',') ===
      (payload.mentionedTeamIds || []).join(',') &&
    reconciledMentions.value.mentionAll === Boolean(payload.mentionAll) &&
    (props.isThread && alsoSendInChannel.value) ===
      Boolean(payload.alsoSendInChannel) &&
    attachments.value.length === payload.attachments.length &&
    attachments.value.every(
      (file, index) => file === payload.attachments[index]
    );

  const resetIfUnchanged = payload => {
    if (!payload || payloadMatches(payload)) reset();
  };

  return {
    content,
    contentFormat,
    isContentEmpty,
    attachments,
    fileInput,
    textInput,
    audioVisible,
    audioBusy,
    audioError,
    startAudio,
    discardAudio,
    finishAudio,
    failAudio,
    resizableEditor,
    editorExpanded,
    editorSizeLabel,
    toggleEditorSize,
    mentionMenuOpen,
    alsoSendInChannel,
    canSend,
    validationError,
    openFilePicker,
    onFilesSelected,
    removeAttachment,
    toggleMentionMenu,
    submit,
    reset,
    resetIfUnchanged,
    onInput,
  };
};
